import { useState } from "react";
import { Avatar, Dropdown, Modal, QRCode } from "antd";
import {
  ApartmentOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  GlobalOutlined,
  QrcodeOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { switchToNonPersonal } from "../store/authSlice";
import { logout as logoutApi } from "../api/auth";
import { clearEmployeeRecord, getAccountIsPersonal } from "../lib/auth";
import { useFullscreen } from "./profileActions";
import { useOrganization } from "../hooks/useOrganization";
import { usePushFeatureEnabled } from "../hooks/usePushSubscription";
import { useIsEdgeLocal } from "../hooks/useEdgeMode";
import PushSettingsModal from "./push/PushSettingsModal";
import ProfileMenu from "./profile/ProfileMenu";
import type { CredentialsMenuEntry } from "./profile/ProfileMenu";
import ProfileMenuRow from "./profile/ProfileMenuRow";
import ChangeCredentialsModal from "./profile/ChangeCredentialsModal";
import ThemeModal from "./profile/ThemeModal";
import { LANGUAGES } from "./profile/languages";
import { modalStyles, modalWrapStyle } from "../utils/modalStyles";
import { blockIfLicenseReadOnly } from "../lib/licenseReadOnly";

const R = {
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  text: "var(--color-ink)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brand: "var(--color-brand-ink)",
  brandTint: "var(--color-brand-tint)",
};

interface ProfileDropdownProps {
  displayName: string;
  username: string;
  access: boolean;
  isNarrow: boolean;
  onLogout: () => void;
  /** Label за бутона за излизане когато access=false. По подразбиране common.logout. */
  logoutLabelKey?: string;
  /** Label за бутона когато access=true (отказване на достъп). По подразбиране modules.revokeAccess. */
  revokeAccessLabelKey?: string;
  /** Компактен режим (свит sidebar): само аватарът с менюто — без
      Backoffice/QR/име. */
  compact?: boolean;
  /** Без текстовете „Backoffice" и името — иконките остават (мониторите ги
      скриват, когато станциите горе нямат място). Текстовете са свити, не
      махнати, за да може да се мери колко място биха освободили
      (`data-collapsible-label`, useNavbarFit.ts). */
  hideLabels?: boolean;
}

export default function ProfileDropdown({
  displayName,
  username,
  access,
  isNarrow,
  onLogout,
  logoutLabelKey = "common.logout",
  revokeAccessLabelKey = "modules.revokeAccess",
  compact = false,
  hideLabels = false,
}: ProfileDropdownProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUserPersonal = useAppSelector((s) => s.auth.personal);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  // Редът „Известия" се крие изцяло, когато BE няма VAPID ключове.
  const pushFeatureEnabled = usePushFeatureEnabled();
  // „Смяна на код или парола" — само за личен акаунт и само за това, което
  // човекът вече има (първа парола/код се задава от Организация). Монитор
  // акаунтът (само модул „Монитори") е общ за кухненските екрани — паролата му
  // се сменя от Организация, както и изходът му я иска. Без интернет редът
  // стои изключен с подсказка: смяната пише в облака.
  const edgeLocal = useIsEdgeLocal();
  const canChangeCredentials = useAppSelector(
    (s) =>
      s.auth.personal === true &&
      !(s.auth.roles.length === 1 && s.auth.roles[0].name === "monitors"),
  );
  const hasCode = useAppSelector(
    (s) => canChangeCredentials && s.auth.user?.has_code === true,
  );
  const hasPassword = useAppSelector(
    (s) => canChangeCredentials && s.auth.user?.has_password === true,
  );
  const credentialsEntry: CredentialsMenuEntry | null =
    hasCode || hasPassword
      ? {
          label:
            hasCode && hasPassword
              ? t("credentials.menuLabel")
              : hasCode
                ? t("credentials.menuLabelCode")
                : t("credentials.menuLabelPassword"),
          offlineHint: edgeLocal ? t("edge.unavailableOffline") : undefined,
        }
      : null;
  // „Цял екран" е самостоятелна икона в лентата (QR кодът е в менюто).
  const fs = useFullscreen();

  // Общ терминал с влязъл служител: „Изход" връща към пин-екрана (смяна на
  // служител), а НЕ пълен logout към /login. Пълният изход на устройството
  // остава поведение на личния акаунт (или на споделения без влязъл служител).
  const isSharedEmployee =
    getAccountIsPersonal() === false && currentUserPersonal === true;

  // Бутон „Backoffice" — само за акаунти с organization_access и не на телефон.
  const orgAccess = useAppSelector(
    (s) => s.auth.user?.organization_access === true,
  );
  const { data: organization } = useOrganization(orgAccess);
  const backofficeUrl =
    orgAccess && organization?.name
      ? `https://${organization.name}.unrealsoft.cloud`
      : null;

  const handleBackToPin = async () => {
    // Guard срещу двоен клик: втори logout след switchToNonPersonal би деактивирал
    // самата споделена сесия (освободил лиценза) вместо да върне към пин.
    if (busy) return;
    setBusy(true);
    try {
      await logoutApi(); // връща session_token на споделения акаунт (един лиценз)
    } catch (e) {
      console.error("Logout error:", e);
    }
    // Човешки жест — служителят си тръгва, затова безусловно. Съседните
    // прозорци виждат триенето през storage event и също падат до ПИН.
    clearEmployeeRecord();
    dispatch(switchToNonPersonal());
    navigate("/", { replace: true });
  };

  // В тестов режим (access) бутонът излиза от тестовия режим през подадения onLogout;
  // иначе при общ терминал със служител връщаме към пин, инак — подадения logout.
  const effectiveAction =
    !access && isSharedEmployee ? handleBackToPin : onLogout;

  return (
    <>
      {/* Backoffice — линк към организацията; скрит на телефон и без
          organization_access. */}
      {backofficeUrl && !isNarrow && !compact && (
        <a
          href={backofficeUrl}
          target="_blank"
          rel="noreferrer"
          title={hideLabels ? t("common.backoffice") : undefined}
          // `!` — цветът на линковете от antd (cssinjs, извън Tailwind слоевете) иначе бие.
          className="flex h-[30px] shrink-0 items-center rounded-[6px] px-[8px] text-[12.5px] font-semibold whitespace-nowrap !text-muted no-underline transition-colors duration-[120ms] hover:bg-shade-1 hover:!text-ink"
        >
          <ApartmentOutlined className="text-[15px]" />
          <span
            data-collapsible-label
            className={`overflow-hidden leading-[1.2] ${hideLabels ? "w-0" : "ml-[6px]"}`}
          >
            {t("common.backoffice")}
          </span>
        </a>
      )}

      {/* Цял екран — самостоятелна икона вън от Dropdown trigger-а, за да не
          отваря менюто (QR кодът е ред В менюто). */}
      {!compact && (
      <button
        type="button"
        title={
          fs.isFullscreen ? t("common.exitFullscreen") : t("common.fullscreen")
        }
        onClick={fs.toggle}
        style={{
          height: 30,
          width: 30,
          borderRadius: 6,
          background: "transparent",
          border: "none",
          color: R.textMuted,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "background 120ms, color 120ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--color-shade-1)";
          e.currentTarget.style.color = R.ink;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = R.textMuted;
        }}
      >
        {fs.isFullscreen ? (
          <FullscreenExitOutlined style={{ fontSize: 15 }} />
        ) : (
          <FullscreenOutlined style={{ fontSize: 15 }} />
        )}
      </button>
      )}

      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        open={open}
        onOpenChange={setOpen}
        dropdownRender={() => (
          <ProfileMenu
            displayName={displayName}
            username={username}
            access={access}
            onLogout={() => {
              setOpen(false);
              effectiveAction();
            }}
            onOpenTheme={() => {
              setOpen(false);
              setThemeOpen(true);
            }}
            onOpenLanguage={() => {
              setOpen(false);
              setLangOpen(true);
            }}
            onOpenQr={() => {
              setOpen(false);
              setQrOpen(true);
            }}
            showPush={pushFeatureEnabled}
            onOpenPush={() => {
              setOpen(false);
              if (blockIfLicenseReadOnly()) return;
              setPushOpen(true);
            }}
            credentials={credentialsEntry}
            onOpenCredentials={() => {
              setOpen(false);
              if (blockIfLicenseReadOnly()) return;
              setCredentialsOpen(true);
            }}
            logoutLabelKey={logoutLabelKey}
            revokeAccessLabelKey={revokeAccessLabelKey}
          />
        )}
      >
        <div className="flex cursor-pointer items-center">
          {displayName && !compact && (
            <span
              data-collapsible-label
              // На телефон името остава, но клампнато, за да не измества лентата.
              className={`truncate text-[12.5px] leading-[1.2] font-semibold text-ink ${
                isNarrow ? "max-w-[110px]" : "max-w-[200px]"
              } ${hideLabels ? "w-0" : "mr-[8px]"}`}
            >
              {displayName}
            </span>
          )}
          <Avatar
            size={28}
            style={{ background: "var(--color-brand)" }}
            icon={<UserOutlined />}
          />
        </div>
      </Dropdown>

      <ThemeModal open={themeOpen} onClose={() => setThemeOpen(false)} />
      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} />
      <QrModal open={qrOpen} onClose={() => setQrOpen(false)} />
      <PushSettingsModal open={pushOpen} onClose={() => setPushOpen(false)} />
      <ChangeCredentialsModal
        open={credentialsOpen}
        onClose={() => setCredentialsOpen(false)}
        hasCode={hasCode}
        hasPassword={hasPassword}
      />
    </>
  );
}

export function QrModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Базовият адрес на ресторанта — без пътя на текущия модул.
  const url = window.location.origin;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnHidden
      width={340}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div
        style={{
          padding: "18px 20px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: R.brandTint,
            color: R.brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <QrcodeOutlined style={{ fontSize: 16 }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: R.ink,
              letterSpacing: -0.2,
            }}
          >
            {t("common.qrTitle")}
          </div>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 500,
              color: R.textMuted,
              marginTop: 2,
            }}
          >
            {t("common.qrSubtitle")}
          </div>
        </div>
      </div>

      <div style={{ height: 1, background: R.border }} />

      <div
        style={{
          padding: "20px 20px 18px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
      >
        <QRCode
          value={url}
          size={216}
          color="#1A1A1A"
          bgColor="#FFFFFF"
          bordered={false}
        />
        <div
          className="font-mono"
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: R.textMuted,
            letterSpacing: 0.3,
            maxWidth: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {url}
        </div>
      </div>
    </Modal>
  );
}

export function LanguageModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith("bg") ? "bg" : "en";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={340}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div
        style={{
          padding: "18px 20px 14px",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: R.brandTint,
            color: R.brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <GlobalOutlined style={{ fontSize: 16 }} />
        </div>
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: R.ink,
            letterSpacing: -0.2,
          }}
        >
          {t("common.chooseLanguage")}
        </div>
      </div>

      <div style={{ height: 1, background: R.border }} />

      <div style={{ padding: 10 }}>
        {LANGUAGES.map((lang) => (
          <ProfileMenuRow
            key={lang.key}
            icon={<span className="text-[15px] leading-none">{lang.flag}</span>}
            label={lang.label}
            active={currentLang === lang.key}
            onClick={() => {
              i18n.changeLanguage(lang.key);
              onClose();
            }}
          />
        ))}
      </div>
    </Modal>
  );
}
